/**
 * Browser-extension store links and browser detection.
 *
 * Shared so the onboarding step and the in-app install prompt cannot drift to
 * different store URLs. Edge gets its own Edge Add-ons listing: the release
 * command publishes to Chrome and Edge together, so neither lags the other.
 */

export const CHROME_URL = 'https://chromewebstore.google.com/detail/linkflare/niadfadjhbojgpajoellkecpebdknilb'
export const EDGE_URL = 'https://microsoftedge.microsoft.com/addons/detail/linkflare/jclcjdbhiofagookbbeffjgflgkmgdcf'
export const FIREFOX_URL = 'https://addons.mozilla.org/en-US/firefox/addon/linkflare-universal-bookmarker/'
// The Safari extension ships inside Linkflare for Mac, so its "store" is the
// Mac App Store listing of the app (live since 2026-10-12).
export const SAFARI_URL = 'https://apps.apple.com/app/linkflare/id6761481280?mt=12'

// The extension docs page and its locale twins (marketing-site/src/i18n/routes.ts).
const EXTENSION_DOCS_PATHS = {
  en: '/docs/extension/',
  de: '/de/hilfe/browser-erweiterung/',
  fr: '/fr/aide/extension-navigateur/',
  es: '/es/ayuda/extension-navegador/',
  pt: '/pt/ajuda/extensao-navegador/',
  it: '/it/guida/estensione-browser/'
}

/** The bookmarklet section of the extension docs in the reader's language, English otherwise. */
export function bookmarkletUrlFor(locale) {
  const path = EXTENSION_DOCS_PATHS[locale] ?? EXTENSION_DOCS_PATHS.en
  return `https://linkflare.io${path}#bookmarklet`
}

/**
 * The Safari extension ships only inside Linkflare for Mac (Mac App Store id
 * 6761481280, live since 2026-10-12), so browser Safari users get the App
 * Store listing of the app (SAFARI_URL) plus a note on enabling the extension.
 *
 * Off means there is nothing to send a browser Safari user to: the install
 * surfaces drop their Safari offer and the in-app prompt points to the
 * bookmarklet and File > Add to Dock instead. That was the state while the
 * app was in App Review; turn it off again if the app is ever pulled.
 *
 * The Mac wrapper is not affected: it embeds the extension and enables it
 * through Safari settings (openSafariPreferences) whatever this says.
 */
export const SAFARI_EXTENSION_LIVE = true

export function detectBrowser(userAgent) {
  const ua = userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '')
  if (/Edg\//.test(ua)) return 'edge'
  if (/Firefox\//.test(ua)) return 'firefox'
  if (/Chrome\//.test(ua)) return 'chrome'
  if (/Safari\//.test(ua)) return 'safari'
  return 'other'
}

/** Store URL for a browser. Unknown browsers get the Chrome listing. */
export function storeUrlFor(browser) {
  if (browser === 'firefox') return FIREFOX_URL
  if (browser === 'edge') return EDGE_URL
  if (browser === 'safari') return SAFARI_URL
  return CHROME_URL
}

/**
 * Whether a browser has a Linkflare extension to install: false only for
 * Safari while SAFARI_EXTENSION_LIVE is off. `safariLive` is a parameter so
 * both states are testable.
 */
export function hasExtensionFor(browser, safariLive = SAFARI_EXTENSION_LIVE) {
  return browser !== 'safari' || safariLive
}

/**
 * False on platforms that cannot install a desktop browser extension at all.
 *
 * Android Chrome serves the Web Store listing with no install control, and the
 * Safari extension is enabled from macOS settings — so on a phone or tablet
 * every store link is a dead end. Surfaces that nudge for the extension must
 * check this or they nag mobile users with a CTA that can never convert.
 *
 * `touchPoints` is a parameter so the iPadOS case is testable: iPadOS 13+
 * reports a desktop `Macintosh` user agent, and touch points are the only
 * reliable tell.
 */
export function isExtensionCapable(userAgent, touchPoints) {
  const ua = userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '')
  const touches = touchPoints ?? (typeof navigator !== 'undefined' ? navigator.maxTouchPoints || 0 : 0)
  if (/Android|iPhone|iPod|iPad/i.test(ua)) return false
  if (/Macintosh/.test(ua) && touches > 1) return false
  return true
}
